import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Button } from "@mui/material";
import toast from "react-hot-toast";

import MarkunreadIcon from '@mui/icons-material/Markunread';
import PasswordIcon from '@mui/icons-material/Password';

import Form from 'react-bootstrap/Form';
import InputField from "../../components/InputField";
import BtnSpinner from "../../../common/btnSpinner";
import useCustomNavigate from "../../../common/hooks/useCustomNavigate";
import { theme } from "../../../theme";
import { submitLogin, firebaseWebTokenRegister, getCustomerLatestJob, getCustomerSettings } from "../service";
import { setCurrentJob, setSettings, setUserInfo } from "../../../storage";
import signInSchema from "./schema/signIn";

const fieldsConfig = [
    {
        label: "Email",
        placeholder: "Email",
        type: "email",
        name: "email",
        width: 12,
        icon: <MarkunreadIcon />
    },
    {
        label: "Password",
        placeholder: "Password",
        type: "password",
        name: "password",
        width: 12,
        icon: <PasswordIcon />
    }
];

const SignInForm = ({setShowForgotPassword, isCheckPrice = false}) => {
    const { navigateToCustom } = useCustomNavigate();
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(signInSchema),
    });

    const onSubmit = useCallback(async (data) => {
        setLoading(true);

        try {
            const response = await submitLogin(data);

            if (response?.status !== "Success") {
                return;
            }else{
                setUserInfo(response?.data);
            }

            if (window.innerWidth < 770) {
                navigateToCustom("/qr/code");
                return;
            }

            const tokenRegisterRes = await firebaseWebTokenRegister(response?.data?.customer_id);
            if (tokenRegisterRes.status === "Failed") {
                toast.error("Firebase Web Token Register Failed. Please try again.");
                return;
            }

            const settingsResponse = await getCustomerSettings();
            if (settingsResponse.status === "Success") {
                setSettings(settingsResponse.settings);

                const latestJobRes = await getCustomerLatestJob(response?.data?.customer_id);
                if (latestJobRes.status === "Success" && latestJobRes?.latest_job) {
                    setCurrentJob({
                        job_id: latestJobRes.latest_job.job_id,
                        job_firebase_document_id: latestJobRes.latest_job.job_firebase_document_id
                    });
                    navigateToCustom(`/current/job/${latestJobRes.latest_job.job_id}`);
                } else {
                    if(isCheckPrice) {
                        navigateToCustom("/home?checkPrice=yes");
                    }else{
                        navigateToCustom("/home");
                    }
                }
            } else {
                toast.error("Failed to fetch customer settings.");
            }

        } catch (error) {
            toast.error("An unexpected error occurred.");
        } finally {
            reset();
            setLoading(false);
        }
    }, [navigateToCustom, reset]);

    const handleForgotPasswordClick = () => {
        // Add forgot password logic here
        setShowForgotPassword(true);
    };

    return (
        <Form role="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
                {fieldsConfig.map((field, index) => (
                    <Grid item xs={field.width} key={index}>
                        <InputField
                            label={field.label}
                            placeholder={field.placeholder}
                            error={errors[field.name]}
                            type={field.type}
                            icon={field.icon}
                            register={register}
                            name={field.name}
                            isRemoveLabel={isCheckPrice}
                        />
                    </Grid>
                ))}
            </Grid>

            <p className="text-sm text-end">
                <Button
                    variant="text"
                    size="small"
                    className={`text-${theme.main} text-gradient font-weight-bold`}
                    onClick={handleForgotPasswordClick}
                >
                    Forgot password
                </Button>
            </p>

            <div className="text-center">
                <button
                    type="submit"
                    className={`btn bg-gradient-${theme.main} w-100 my-1 mb-0`}
                    disabled={loading}
                >
                    {loading ? <BtnSpinner /> : "Sign up"}
                </button>
            </div>
        </Form>
    );
};

export default SignInForm;